import createDraftOrder from './draftOrder';
import CheckoutDiscountCode from "./applyDiscountCode";
import AddressAutocomplete from "./addressAutocomplete";
import CheckoutCountryGuard from "./countryGuard";
import InputValidation from "./inputValidation";
import { CheckoutTracking } from './tracking';
import { applyRedeemGiftCard } from './applyRedeemGiftCard';
import { MediaSelector } from './mediaSelector';

class CheckoutForm {
  constructor(container) {
    console.log("checkout-form")
    this.container = container;
    this.discountCode = new CheckoutDiscountCode(this.container);
    this.inputValidation = new InputValidation(this.container);
    this.shopLocale = Theme.settings.shop_locale;
    this.paymentLoading = document.querySelector('.payment-loading');
    this.formData = {};
    this.eventToShowDeliveryMethodsWasTriggered = false;

    if (!this.container) {
      return;
    }
    this.formWrapper = document.querySelector('#wellvita-payment-form');

    this.form = this.container.querySelector('#wellvita-payment-form');
    if (!this.form) {
        return;
    }

    this.submitButtons = this.container.querySelectorAll('button[type="submit"]');
    if(!this.submitButtons) return;
    this.init();
  }

  init() {
    this.setupLiveValidation();
    this.setupSubmitValidation();
    this.onPermissionsChange();

    const mediaSelectorContainer = this.container.querySelector('.media-selector');
    if (mediaSelectorContainer) {
      this.mediaSelector = new MediaSelector(mediaSelectorContainer);
    }
    this.countryGuard = new CheckoutCountryGuard(this.container);
    
    const isAddressSuggestionsEnabled = Theme.settings.enable_checkout_address_suggestions || false; 
    if (isAddressSuggestionsEnabled) {
      window.addEventListener("load", () => {
        new AddressAutocomplete();
      });
      document.addEventListener('address:autocomplete', (event) => {
        this.onAddressAutocomplete(event);
      });
    }
    document.addEventListener('checkout:country-changed', (event) => {
      this.onCountryChanged(event);
    });

    if(this.formWrapper){
      this.formWrapper.addEventListener('form-submit', (event) => {
        event.preventDefault();
        this.formWrapper.submit();
      })
    } 
  }

  onAddressAutocomplete(event) {
    const address = event.detail.place;
    if(!address) return;
    const {city, postalCode, street, streetNumber} = address;
    const autofillPostcodeAndCity = Theme.settings.enable_checkout_address_autofill !== false;
    let fullStreet = '';
    if(street) {
      const streetInput = document.querySelector('gmp-place-autocomplete').shadowRoot.querySelector('input');
      fullStreet = street;
      if(streetNumber) fullStreet += ` ${streetNumber}`;
      if(streetInput){
        streetInput.value = fullStreet;
        const checkoutAddressInput = document.querySelector('input[name="checkout-address"]');
        checkoutAddressInput.value = fullStreet;
      }
    }
    if(autofillPostcodeAndCity && postalCode) {
      const postalCodeInput = this.container?.querySelector('input[name="checkout-postcode"]');
      if(postalCodeInput){
        postalCodeInput.value = postalCode;
      }
    }
    if(autofillPostcodeAndCity && city) {
      const cityInput = this.container?.querySelector('input[name="checkout-city"]');
      if(cityInput) cityInput.value = city;
    }

    const details = {
      address: fullStreet,
      city: autofillPostcodeAndCity ? city : undefined,
      postcode: autofillPostcodeAndCity ? postalCode : undefined
    };

    const autocompleteFilledEvent = new CustomEvent('address:autocomplete-filled', {
      detail: details
    });
    document.dispatchEvent(autocompleteFilledEvent);
  }

  getDiscountInstance() {
    return this.discountCode;
  }

  onCountryChanged(event) {
    const dialCode = event.detail.dialCode;
    const country = event.detail.country;
    const phoneInput = this.container.querySelector('#checkout-phone');
    if(phoneInput){
      const phoneInputValue = phoneInput.value;
      const oldDialCode = phoneInput.getAttribute('data-dialcode') || '';
      if(phoneInputValue.includes(`+${oldDialCode}`) && oldDialCode !== dialCode){
        phoneInput.value = phoneInputValue.replace(`+${oldDialCode}`, `+${dialCode}`);
      }
      else {
        if(!phoneInputValue.startsWith('+')){
          phoneInput.value = `+${dialCode}` + phoneInputValue;
        }
      }
      phoneInput.setAttribute('data-dialcode', `${dialCode}`); 
    }
  }

  setupLiveValidation() {
    const inputs = this.container.querySelectorAll('input[checkout-input][required]');
    inputs.forEach(input => {
      input.addEventListener('click', () => {
        this.clearError(input.closest('[data-input-wrapper]')); 
      });
      input.addEventListener('blur', () => this.inputValidation.validateInput(input));
      if(input.id === 'checkout-address') {
        input.addEventListener('auto-complete-input:changed', () => {
          this.inputValidation.validateInput(input);
        });
      }
    });
  }
 
  showError(wrapper) {
    if (!wrapper) return;
    wrapper.classList.add('has-error');
  }

  clearError(wrapper) {
    if (!wrapper) return;
    wrapper.classList.remove('has-error');
  }

  setupSubmitValidation() {
    this.submitButtons.forEach(submitButton => {
      submitButton.addEventListener('click', () => this.validateForm());
    });
  }

  validatePermissions() {
    let arePermissionsValid = true;
    const permissions = this.container.querySelectorAll('input[data-checkout-permission]');
    const visiblePermissions = Array.from(permissions).filter(element => this.isElementVisible(element));
    visiblePermissions.forEach(permission => {
      const isChecked = permission.checked;
      const wrapper = permission.closest('.permission-item');
      if(!wrapper) return;
      if(isChecked) {
        this.clearError(wrapper);
      } else {
        this.showError(wrapper);
        arePermissionsValid = false;
      }
    });
    return arePermissionsValid;
  }

  onPermissionsChange() {
    const permissions = this.container.querySelectorAll('input[data-checkout-permission]');
    permissions.forEach(permission => {
      permission.addEventListener('change', () => {
        const wrapper = permission.closest('.permission-item');
        if(permission.checked && wrapper){
          this.clearError(wrapper);
        } else {
          this.showError(wrapper);
        }
      });
    });
  }

  async validateForm() {
    const inputs = this.container.querySelectorAll('input[checkout-input][required]');
    inputs.forEach(input => {
      this.inputValidation.validateInput(input);
    });
    const arePermissionsValid = this.validatePermissions();
    if(!arePermissionsValid) return;
    const areInputsValid = this.retrieveFormData();
    if(!areInputsValid) return;
    await this.fireFinalCheckoutTrackingEvents();
    const apiPaymentMethodInput = document.querySelector('#wellvita-payment-form input[name="APIPaymentMethod"]');
    if(apiPaymentMethodInput){
      this.fillInForm(this.formData);
      this.setPaymentLoading();
      const apiPaymentMethodInputVal = apiPaymentMethodInput.getAttribute('value');
      const {draftOrderName, draftIdFormatted, draftId} = await this.prepareDraftOrder(apiPaymentMethodInputVal);
      if(window.checkout.giftCardAmount > 0 && window.checkout.giftCardCode){
        const isPaidFullyWithGiftCard = await this.submitOrderPaidFullyWithGiftCard(draftOrderName, draftIdFormatted, draftId);
        if(isPaidFullyWithGiftCard) return;
      }
      if(apiPaymentMethodInputVal === 'Faktura') return this.markOrderAsPaidManually(draftOrderName, draftIdFormatted, draftId, apiPaymentMethodInputVal);
      return this.submitForm(draftOrderName, draftIdFormatted, draftId);
    } 
  }

  async submitOrderPaidFullyWithGiftCard(draftOrderName, draftIdFormatted, draftId){
    if(window.checkout.isFreeDeliveryApplied && window.checkout.manualDiscountCode && window.checkout.totalPrice === 0){
      this.markOrderAsPaidManually(draftOrderName, draftIdFormatted, draftId);
      return true;
    }
    return false;
  }


  async prepareDraftOrder(paymentMethod) {
    try {
      const countryCode = document.querySelector('input[name="APIShopCountryCode"]');
      let countryCodeValue;
      if(countryCode ) countryCodeValue = countryCode.value;
      const { draftOrder } = await createDraftOrder(this.formData, paymentMethod, countryCodeValue);
      if(draftOrder){
        let draftOrderName = draftOrder.name;
        const { draftIdFormatted, draftId } = this.modifyDraftOrderId(draftOrder, draftOrderName);
        if(!draftIdFormatted || ! draftId || !draftOrderName) throw new Error("❌ Draft order failed");
        return {draftOrderName: draftOrderName, draftIdFormatted: draftIdFormatted, draftId: draftId};
      }
    } catch (error) {
      console.error("❌ Draft order failed:", error);
    }
  }

  retrieveFormData() {
    this.formData = this.getFormData();
    const allErrorElements = Array.from(this.container.querySelectorAll('.has-error'));
    const visibleErrorElements = allErrorElements.filter(element => this.isElementVisible(element));
    if (visibleErrorElements.length > 0) return false;
    return true;
  }

  isElementVisible(element) {
    return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
  }

  // Get all visible input values with checkout-input attribute
  getFormData() {
    const inputs = this.container.querySelectorAll('[checkout-input]');
    const data = {};
    
    inputs.forEach(input => {
      const wrapper = input.closest('[data-input-wrapper]');
      if (wrapper && this.isElementVisible(wrapper)) {
        const inputName = input.getAttribute('name') || input.id || '';
        if (inputName) {
          data[inputName] = input.value.trim();
        }
      }
    });
    const lineItemsData = this.getLineItemsData();
    data.lineItems = lineItemsData;
    const manualDiscountCode = this.checkManualDiscountCode();
    if(manualDiscountCode){
      data.appliedDiscount = manualDiscountCode;
    }
    return data;
  }

  checkManualDiscountCode() {
    const discountCode = window.checkout.manualDiscountCode;
    const discountAmount = window.checkout.discountAmountCombined;
    if(discountCode && discountAmount != null){
      let discountAmountNumber = parseFloat(discountAmount) || 0;
      if(isNaN(discountAmountNumber)) return null;
      discountAmountNumber = discountAmountNumber / 100;
      return{
        "description": discountCode,
        "value": discountAmountNumber,
        "amount": discountAmountNumber,
        "valueType": "FIXED_AMOUNT",
        "title": discountCode
      }
    }
    return null;
  }

  fillInForm(formData) {
    const inputCName = this.formWrapper.querySelector('input[name="APICName"]');
    const inputCAddress = this.formWrapper.querySelector('input[name="APICAddress"]');
    const inputCZipCode = this.formWrapper.querySelector('input[name="APICZipCode"]');
    const inputCCity = this.formWrapper.querySelector('input[name="APICCity"]');
    const inputCEmail = this.formWrapper.querySelector('input[name="APICEmail"]');
    const inputCPhone = this.formWrapper.querySelector('input[name="APICPhone"]');
    const inputCNumber = this.formWrapper.querySelector('input[name="APICNumber"]');

    const inputDName = this.formWrapper.querySelector('input[name="APIDName"]');
    const inputDAddress = this.formWrapper.querySelector('input[name="APIDAddress"]');
    const inputDZipCode = this.formWrapper.querySelector('input[name="APIDZipCode"]');
    const inputDCity = this.formWrapper.querySelector('input[name="APIDCity"]');
    const inputDEmail = this.formWrapper.querySelector('input[name="APIDEmail"]');
    const inputDPhone = this.formWrapper.querySelector('input[name="APIDPhone"]');

    if(inputCName) inputCName.setAttribute('value', formData?.['checkout-fname'] + ' ' + formData?.['checkout-lname']);
    if(inputCAddress) inputCAddress.setAttribute('value', formData?.['checkout-address']);
    if(inputCZipCode) inputCZipCode.setAttribute('value', formData?.['checkout-postcode']);
    if(inputCCity) inputCCity.setAttribute('value', formData?.['checkout-city']);
    if(inputCEmail) inputCEmail.setAttribute('value', formData?.['checkout-email']);
    if(inputCPhone) inputCPhone.setAttribute('value', formData?.['checkout-phone']);
    if(inputCNumber) inputCNumber.setAttribute('value', formData?.['checkout-phone']);
    if(inputDName) inputDName.setAttribute('value', formData?.['checkout-fname'] + ' ' + formData?.['checkout-lname']);
    if(inputDAddress) inputDAddress.setAttribute('value', formData?.['checkout-address']);
    if(inputDZipCode) inputDZipCode.setAttribute('value', formData?.['checkout-postcode']);
    if(inputDCity) inputDCity.setAttribute('value', formData?.['checkout-city']);
    if(inputDEmail) inputDEmail.setAttribute('value', formData?.['checkout-email']);
    if(inputDPhone) inputDPhone.setAttribute('value', formData?.['checkout-phone']);
  }

  getLineItemsData() {
    const checkoutOrderCart = this.container.querySelector('.checkout-order__card[data-is-main="true"]')
      || Array.from(this.container.querySelectorAll('.checkout-order__card')).find(cart => this.isElementVisible(cart));
    if(!checkoutOrderCart) return [];
    const lineItems = checkoutOrderCart.querySelectorAll('.cart-line__item');
    if(!lineItems) return [];
    const lineData = [];
    const lineItemsArray = Array.from(lineItems);
    lineItemsArray.forEach(lineItem => {
      const lineItemVariantId = lineItem?.dataset?.itemVariantId;
      const lineItemVariantTitle = lineItem?.dataset?.itemVariantTitle;
      const quantity = parseInt(lineItem?.dataset?.itemQuantity) || 1;

      // All money here is in MINOR units (cents) to match the data attributes:
      // - unit price (single, full) ........ data-item-original-unit-price-raw
      // - line total (after all discounts) .. data-item-original-total-price (= item.final_line_price)
      // - free-shipping portion ............. data-item-fs-amount (already relocated to the delivery line)
      const unitPriceCents = parseFloat(lineItem?.dataset?.itemOriginalUnitPriceRaw) || 0;
      const finalLineCents = parseFloat(lineItem?.dataset?.itemOriginalTotalPrice) || 0;
      const fsLineCents = parseFloat(lineItem?.dataset?.itemFsAmount) || 0;
      const discountTitle = lineItem?.dataset?.itemDiscountTitle;

      const fullLineCents = unitPriceCents * quantity;
      const productDiscountCents = Math.max(0, (fullLineCents - finalLineCents) - fsLineCents);

      const lineItemData = {
        variantId: lineItemVariantId,
        title: lineItemVariantTitle,
        quantity: quantity,
        originalUnitPrice: unitPriceCents / 100, // full unit price, major units
      };

      if (productDiscountCents > 0 && discountTitle) {
        // Shopify applies FIXED_AMOUNT line discounts PER UNIT on draft orders, so send the
        // per-unit share — sending the whole-line amount gets multiplied by quantity again.
        if (productDiscountCents % quantity !== 0) {
          console.warn(`Line discount ${productDiscountCents} øre not evenly divisible by quantity ${quantity} (variant ${lineItemVariantId}); rounding per-unit amount`);
        }
        const discountValue = Math.round(productDiscountCents / quantity) / 100; // per-unit, major units
        lineItemData.appliedDiscount = {
          description: "",
          value: discountValue,
          amount: discountValue,
          valueType: "FIXED_AMOUNT",
          title: discountTitle
        };
      }

      lineData.push(lineItemData);
    });

    return lineData;
  }
  setPaymentLoading() {
    this.paymentLoading.classList.remove('d-none');
    document.querySelector('body').classList.add('no-scroll');
  }

  async submitForm(draftOrderName, draftIdFormatted, draftId) {
    try {
      if (draftIdFormatted && draftId && draftOrderName){
        const hashObj = await this.createHash(draftIdFormatted);
        if(!hashObj) throw new Error("❌ Hash failed");
        const { hash, totalPriceVal } = hashObj;

        // Store checkout data for tracking before redirecting
        if (window.checkout && typeof Storage !== 'undefined') {
          sessionStorage.setItem('checkout_tracking_data', JSON.stringify({
            totalPrice: window.checkout.totalPrice,
            deliveryPrice: window.checkout.deliveryPrice,
            vatPrice: window.checkout.vatPrice,
            discountCode: window.checkout.discountCode,
            discountAmount: window.checkout.discountAmount,
            currency: Theme.settings.shop_currency || 'DKK'
          }));
        }
        // Store cart data for items
        try {
          const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
          const cartData = await cartResponse.json();
          if (cartData && cartData.items) {
            sessionStorage.setItem('checkout_cart_data', JSON.stringify(cartData));
          }
        } catch (e) {
          console.warn('Could not fetch cart data for tracking:', e);
        }

        this.updateOrderForm(hash, totalPriceVal, draftOrderName, draftId, draftIdFormatted);
        const event = new Event('form-submit', { bubbles: true, cancelable: true });
        const formSubmit = this.formWrapper.dispatchEvent(event);
        this.formWrapper.submit();
      }
    } catch (error) {
      console.error("❌ Draft order failed:", error);
    }
  } 

  modifyDraftOrderId(draftOrder, draftOrderName) {
    let draftOrderId = draftOrder.id;
    if(!draftOrderId) throw new Error("❌ Draft order failed:", draftOrder.error || draftOrder);
    if(draftOrderId.includes("/")) draftOrderId = Number(draftOrderId.split('/').pop());
    draftOrderName = formatDraftName(draftOrderName);

    const prependedCountryCode = Theme.settings.country_iso_code;
    const hardcodedPrefix = '3';
    const paddedDraftOrderName = String(draftOrderName).padStart(5, '0');

    return {
      draftIdFormatted: `${prependedCountryCode}${hardcodedPrefix}${paddedDraftOrderName}`,
      draftId: draftOrderId
    };
  }

  async markOrderAsPaidManually(draftOrderName, draftIdFormatted, draftOrderId) {
    try {
      if(draftIdFormatted && draftOrderId && draftOrderName){
        const marketingConsent = this.checkCustomerMarketingConsent();
        const _theme = window.theme || window.Theme;
        const fulfillOrder = await fetch(`https://wellvita-api.netlify.app/.netlify/functions/fulfillDraftOrder?store=${_theme.settings.store_handle}&draft_order_id=${draftOrderId}&shopify_draft_order_id=${draftIdFormatted}&marketing_consent=${marketingConsent}&lang=${_theme.settings.shop_locale}&shipping_code=${encodeURIComponent(window.checkout.deliveryCode)}`);
        if(!fulfillOrder) throw new Error("❌ Fulfill order failed");
        const fulfillOrderData = await fulfillOrder.json();
        if(!fulfillOrderData) throw new Error("❌ Fulfill order failed");

        // Store checkout data for tracking before redirecting because it will be lost after redirect
        if (window.checkout && typeof Storage !== 'undefined') {
          sessionStorage.setItem('checkout_tracking_data', JSON.stringify({
            totalPrice: window.checkout.totalPrice,
            deliveryPrice: window.checkout.deliveryPrice,
            vatPrice: window.checkout.vatPrice,
            discountCode: window.checkout.discountCode,
            discountAmount: window.checkout.discountAmount,
            currency: Theme.settings.shop_currency || 'DKK'
          }));
        }

        // Store cart data for items
        try {
          const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
          const cartData = await cartResponse.json();
          if (cartData && cartData.items) {
            sessionStorage.setItem('checkout_cart_data', JSON.stringify(cartData));
          }
        } catch (e) {
          console.warn('Could not fetch cart data for tracking:', e);
        }

        const statusPageUrl = fulfillOrderData?.data?.order?.statusPageUrl;
        if (statusPageUrl) return window.location.href = statusPageUrl;
      }
    } 
    catch (error) {
      console.error("❌ Draft order failed:", error);
    }
  }
  
  updateOrderForm(hash, totalPriceVal, draftName, draftId, draftIdFormatted){
    const orderAPIKeyInput = this.formWrapper.querySelector('input[name="APIkey"]');
    const orderIDInput = this.formWrapper.querySelector('input[name="APIOrderID"]');
    const orderTotalAmountInput = this.formWrapper.querySelector('input[name="APITotalAmount"]');
    const orderCallBackServerUrlInput = this.formWrapper.querySelector('input[name="APICallBackServerUrl"]');
    const orderCallBackOKServerUrlInput = this.formWrapper.querySelector('input[name="APIFullCallBackOKUrl"]');
    const marketingConsent = this.checkCustomerMarketingConsent();
    draftName = formatDraftName(draftName);

    if(orderAPIKeyInput && orderIDInput && orderTotalAmountInput && orderCallBackServerUrlInput && orderCallBackOKServerUrlInput){
      const orderCallBackOkServerURLValue = orderCallBackOKServerUrlInput.getAttribute('value');
      const _theme = window.theme || window.Theme;
      const callbackServerURL = `wellvita-api.netlify.app/.netlify/functions/processPaymentCallback?draft_order_name=${draftName}&draft_order_id=${draftId}&shopify_draft_order_id=${draftIdFormatted}&store_name=${_theme.settings.store_handle}&marketing_consent=${marketingConsent}&lang=${_theme.settings.shop_locale}&shipping_code=${encodeURIComponent(window.checkout.deliveryCode)}`;
      const callbackOKServerURL = `${orderCallBackOkServerURLValue}/pages/order-status-page-temporary?draft_id=${draftId}&store_name=${_theme.settings.store_handle}`;
      orderIDInput.setAttribute('value', draftIdFormatted);
      orderAPIKeyInput.setAttribute('value', hash);
      orderTotalAmountInput.setAttribute('value', totalPriceVal);
      orderCallBackServerUrlInput.setAttribute('value', callbackServerURL);
      orderCallBackOKServerUrlInput.setAttribute('value', callbackOKServerURL);
    }
  }

  checkCustomerMarketingConsent() {
    const marketingInputs = document.querySelectorAll('input[marketing-consent]');
    const visibleMarketingInput = Array.from(marketingInputs).find( (input) =>  this.isElementVisible(input) );
    if (!visibleMarketingInput) return false;
    return visibleMarketingInput.checked;
  }

  async createHash(orderId){
    if(!orderId) return;
    const orderAPIKey = this.formWrapper.querySelector('input[name="APIkey"]');
    const totalPrice = this.container.querySelector('.checkout-order__card[data-is-main="true"] span[data-checkout-total]');
    if(!orderAPIKey || !totalPrice) return;
    const totalPriceVal = totalPrice.dataset.totalPriceFormattedNoCurrency;
    if(!totalPriceVal || totalPriceVal === "" || totalPriceVal === '0' || totalPriceVal === 0) return;
    const URL = `https://wellvita-api.netlify.app/.netlify/functions/createHash?totalAmount=${totalPriceVal}&orderId=${orderId}&store_name=${Theme.settings.store_handle}`;
    try {
      const response = await fetch(URL);
      const responseBody = await response.json();
      if(!responseBody) throw new Error('No data received');
      if(responseBody.success){
        const hash = responseBody.data;
        return {hash, totalPriceVal};
      }
    } 
    catch (e) {
      console.error("❌ Hash creation failed:", e);
      throw new Error(e);
    }
  }
  async fireFinalCheckoutTrackingEvents() {
    const tracking = new CheckoutTracking();
    await tracking.init();
    tracking.addShippingInfo();
    tracking.addPaymentInfo();
  }
}

function formatDraftName(name){
  return name.replace(/\D/g, '');
}

export default CheckoutForm;

